import { t } from "@lingui/core/macro";
import { assertNever } from "@regrello/core-utils";
import type {
  AiAgentV4Type,
  UserAccessLevel,
  UserBaseFields,
  UserFields,
  UserWithAiAgentBaseFields,
} from "@regrello/graphql-api";

/** The email prefix that identifies an AI Agent V4 user */
export const AI_AGENT_V4_EMAIL_PREFIX = "agent-v4";

/** The email domain for Regrello users */
export const REGRELLO_EMAIL_DOMAIN = "regrello.com";

export function getUserAvatarScope(user: Pick<UserFields, "accessLevel">): "internal" | "external" | "limited" {
  switch (user.accessLevel) {
    case "INTERNAL":
      return "internal";
    case "EXTERNAL":
      return "external";
    case "LIMITED":
      return "limited";
    default:
      assertNever(user.accessLevel);
  }
}

export function getUserAccessLevelDisplayString(user: Partial<Pick<UserFields, "accessLevel">>): string {
  switch (user.accessLevel) {
    case undefined:
      return t`No access`;
    case "INTERNAL":
      return t`Internal`;
    case "EXTERNAL":
      return t`External`;
    case "LIMITED":
      return t`Limited`;
    default:
      assertNever(user.accessLevel);
  }
}

export function getUserAccessLevelFromDisplayString(displayString: string): UserAccessLevel | undefined {
  switch (displayString) {
    case t`No access`:
      return undefined;
    case t`Internal`:
      return "INTERNAL";
    case t`External`:
      return "EXTERNAL";
    case t`Limited`:
      return "LIMITED";
    default:
      return undefined;
  }
}

/**
 * Checks if a user is an AI Agent V4 based on their email prefix and user type.
 *
 * @param user - The user to check
 * @returns true if the user is an AI Agent V4, false otherwise
 */
export function isUserAiAgentV4(user?: UserBaseFields | null): boolean {
  if (user?.userType !== "AI_AGENT_ACCOUNT") {
    return false;
  }

  return user.email.startsWith(AI_AGENT_V4_EMAIL_PREFIX) && user.email.endsWith(`@${REGRELLO_EMAIL_DOMAIN}`);
}

/**
 * Gets the V4 type of an AI agent if it exists.
 *
 * @param user - The user to check
 * @returns The V4Type if the user is an AI Agent V4, undefined otherwise
 */
export function getAiAgentV4Type(user: UserWithAiAgentBaseFields | undefined): AiAgentV4Type | undefined {
  if (user == null || !isUserAiAgentV4(user)) {
    return undefined;
  }

  return user.aiAgentType?.v4Type;
}
